import { Link } from '@inertiajs/react';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import type { JudgeBallot } from '@/types';
import { CriterionName } from './CriterionName';

export function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="border-2 border-primary/40 p-6 text-center text-primary/70 text-sm">
      {children}
    </div>
  );
}

// The top of the stage page and of its final check: where to go back to, the stage,
// the "closed" banner once the judge finalised it, and the criteria strip.
export function BallotHeader({
  ballot,
  judgeName,
  eventName,
  back,
  kicker,
  summary,
}: {
  ballot: JudgeBallot;
  judgeName: string;
  eventName: string;
  back: { href: string; label: string };
  kicker?: string;
  summary: ReactNode;
}) {
  return (
    <>
      <div className="flex flex-col gap-4">
        <Link
          href={back.href}
          className="flex w-fit items-center gap-1 text-primary/70 text-sm hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          {back.label}
        </Link>
        <div>
          {kicker && (
            <p className="mb-2 font-bold text-primary text-sm uppercase">
              {kicker}
            </p>
          )}
          <h1 className="inline-block bg-primary px-2 py-1 font-bold text-2xl text-background sm:text-3xl">
            {ballot.stageName}
          </h1>
          <p className="mt-3 text-primary/70 text-sm">
            {judgeName} · Jurado en {eventName}
          </p>
        </div>
      </div>

      {ballot.completedAt && (
        <div className="flex items-start gap-3 border-2 border-primary bg-primary p-4 text-background">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="text-sm">
            <p className="font-bold">Evaluación finalizada</p>
            <p>
              Finalizaste este stage el {formatDateTime(ballot.completedAt)}.
              Estas son tus notas finales y ya no se pueden cambiar.
            </p>
          </div>
        </div>
      )}

      {ballot.criteria.length === 0 ? (
        <Notice>
          Todavía no hay criterios de evaluación. Vuelve a cargar en un rato.
        </Notice>
      ) : (
        <div className="flex flex-col gap-3 border-2 border-primary p-4 text-primary text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {ballot.criteria.map((criterion) => (
              <span key={criterion.id}>
                <CriterionName criterion={criterion} className="font-bold" />{' '}
                <span className="text-primary/60">{criterion.weight}%</span>
              </span>
            ))}
          </div>
          <span className="shrink-0 text-primary/70">{summary}</span>
        </div>
      )}
    </>
  );
}
